import PropTypes from 'prop-types';
import { useRef, useMemo } from 'react';
import * as yup from 'yup';

import { useAuthContext } from 'context/authContext';
import CrudCard from '../../common/CrudCard/CrudCard';
import { useSponsorshipCosts, useCreateSponsorshipCost, useUpdateSponsorshipCost, useDeleteSponsorshipCost, useSponsorshipTypes } from 'hooks/useBeneficiaries';

/* -------------------------------------------------------------------------- */
function SponsorshipCosts({ sectionRef, sideNavLink, subject }) {
  const cardRef = useRef();
  const { organization } = useAuthContext();
  const { sponsorshipCosts } = useSponsorshipCosts();
  const { createSponsorshipCost, isCreating } = useCreateSponsorshipCost();
  const { updateSponsorshipCost, isUpdating } = useUpdateSponsorshipCost();
  const { deleteSponsorshipCost, isDeleting } = useDeleteSponsorshipCost();
  const { sponsorshipTypes } = useSponsorshipTypes();

  const handleCreate = ({ amount, sponsorship_type_id }) => {
    createSponsorshipCost({
      amount,
      organisation_id: organization.id,
      billing_frequency: 'Monthly',
      sponsorship_type_id
    }, {
      onSuccess: () => {
        cardRef.current.onCreationSuccess();
      }
    });
  };

  const handleEdit = ({ id, data }) => {
    updateSponsorshipCost({
      id,
      data: {
        amount: data.amount,
        sponsorship_type_id: data.sponsorship_type_id
      }
    }, {
      onSuccess: () => {
        cardRef.current.onEditSuccess();
      }
    });
  };

  const handleDelete = (id) => {
    deleteSponsorshipCost(id, {
      onSuccess: () => {
        cardRef.current.onDeleteSuccess();
      }
    });
  };

  const list = useMemo(() => {
    // Helper to get type name - looks up from sponsorshipTypes list
    const getTypeName = (typeId) => {
      const type = sponsorshipTypes?.find(t => t.id === typeId);
      return type?.type || '-';
    };

    return sponsorshipCosts?.map(item => {
      return {
        id: item.id,
        isOrgLevel: !item.branch_id,
        values: [
          {
            display: item.sponsorship_type?.type || getTypeName(item.sponsorship_type_id),
            value: item.sponsorship_type_id,
            name: 'sponsorship_type_id',
            label: 'Type'
          },
          {
            display: `£${item.amount}/month`,
            value: item.amount,
            name: 'amount',
            label: 'Monthly Cost'
          }
        ]
      };
    });
  }, [sponsorshipCosts, sponsorshipTypes]);

  const sponsorshipTypeOptions = useMemo(() => {
    return sponsorshipTypes?.map(({ id, type }) => ({ label: type, value: id }));
  }, [sponsorshipTypes]);

  return (
    <CrudCard
      ref={cardRef}
      sectionRef={sectionRef}
      sideNavLink={sideNavLink}
      createData={handleCreate}
      editData={handleEdit}
      deleteData={handleDelete}
      createLoading={isCreating}
      updateLoading={isUpdating}
      deleteLoading={isDeleting}
      formTitle={'Add Sponsorship Cost'}
      listTitle={'Current Sponsorship Costs'}
      title={'Sponsorship Costs'}
      inputs={[
        {
          name: 'sponsorship_type_id',
          placeholder: 'Sponsorship Type',
          type: 'select',
          options: sponsorshipTypeOptions,
          required: true,
          value: '',
          validation: yup.string().required('Sponsorship Type is required')
        },
        {
          name: 'amount',
          placeholder: 'Monthly Amount',
          type: 'number',
          required: true,
          value: '',
          validation: yup.number().required('Amount is required').positive('Amount must be positive')
        }
      ]}
      list={list}
      subject={subject}
    />
  );
}

SponsorshipCosts.propTypes = {
  sectionRef: PropTypes.any,
  sideNavLink: PropTypes.any,
  subject: PropTypes.string
};

export default SponsorshipCosts;
